import React, { useState, useEffect } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Paper,
  Box,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Avatar
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Group as GroupIcon } from '@mui/icons-material';
import SkillForm from './SkillForm';
import { useSkills } from '../../contexts/SkillsContext';

const SkillCard = ({ skill, onEdit, onDelete, hasPermission }) => {

  return (
    <Card 
      sx={{ 
        position: 'relative',
        '&:hover': {
          boxShadow: (theme) => theme.shadows[4],
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out'
        }
      }}
    >
      <CardContent sx={{ pb: '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '6px',
              backgroundColor: skill.color,
              mr: 1.5,
              border: '2px solid',
              borderColor: `${skill.color}40`
            }}
          />
          <Typography 
            variant="h6" 
            sx={{ 
              fontSize: '1.1rem',
              fontWeight: 500,
              flex: 1
            }}
          >
            {skill.name}
          </Typography>

          {skill.critical && (
            <Chip 
              label="Critique" 
              size="small" 
              color="error"
              sx={{ 
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 600,
                mr: 1
              }}
            />
          )}

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={!hasPermission?.('edit') ? 'Vous n\'avez pas la permission de modifier cette compétence' : 'Modifier'}>
              <span>
                <IconButton 
                  size="small" 
                  onClick={() => onEdit(skill)}
                  disabled={!hasPermission?.('edit')}
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': { 
                      color: 'primary.main',
                      backgroundColor: 'primary.lighter'
                    }
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={!hasPermission?.('delete') ? 'Vous n\'avez pas la permission de supprimer cette compétence' : 'Supprimer'}>
              <span>
                <IconButton 
                  size="small" 
                  onClick={() => onDelete(skill)}
                  disabled={!hasPermission?.('delete')}
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': { 
                      color: 'error.main',
                      backgroundColor: 'error.lighter'
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        
        <Box sx={{ position: 'relative', mt: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mb: 0.5 
          }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Taux de couverture
            </Typography>
            <Typography 
              variant="body2" 
              color="text.primary"
              sx={{ fontWeight: 600 }}
            >
              {skill.coverageIndex}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={skill.coverageIndex}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: `${skill.color}20`,
              '& .MuiLinearProgress-bar': {
                backgroundColor: skill.color,
                borderRadius: 4
              }
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

const SkillsList = ({ onEdit, onDelete, hasPermission }) => {
  const { skills, loading } = useSkills();

  // Filtrer les compétences invalides (sans id ou sans nom) et trier alphabétiquement
  const validSkills = skills
    .filter(skill => skill && skill.id && skill.name)
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Grid container spacing={2}>
      {validSkills.map((skill) => (
        <Grid item xs={12} sm={6} md={4} key={skill.id}>
          <SkillCard
            skill={skill}
            onEdit={onEdit}
            onDelete={onDelete}
            hasPermission={hasPermission}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default SkillsList; 